/**
 * Single orchestrator that fans every call out to all registered adapters.
 * This is the only class the rest of the app is allowed to depend on
 * (directly, or via the useAnalytics()/hooks in ../hooks) — it never knows
 * about GA4 specifically, so adding/removing/swapping destinations only
 * means changing the adapter list passed to init().
 *
 * It's a plain module singleton (not React state) on purpose: non-React code
 * such as redux-saga (e.g. login/logout sagas) needs to fire events and
 * identify users too, and shouldn't need a hook to do it.
 */
class AnalyticsService {
  constructor() {
    this.adapters = [];
    this.initialized = false;
    // Calls made before init() (e.g. a saga that resolves before the
    // AnalyticsProvider mounts) are queued and replayed once adapters exist.
    this.queue = [];
  }

  /** @param {import('./AnalyticsAdapter').default[]} adapters */
  init(adapters = []) {
    if (this.initialized) return;
    this.adapters = adapters.filter(Boolean);
    this.adapters.forEach(adapter => adapter.initialize());
    this.initialized = true;
    this.queue.forEach(({ method, args }) => this._dispatch(method, args));
    this.queue = [];
  }

  _dispatch(method, args) {
    this.adapters.forEach(adapter => {
      try {
        adapter[method](...args);
      } catch (err) {
        // A single misbehaving destination should never break tracking for
        // the others, or break the feature the user is actually using.
        // eslint-disable-next-line no-console
        console.error(`[analytics] ${adapter.constructor.name}.${method} failed`, err);
      }
    });
  }

  _call(method, args) {
    if (!this.initialized) {
      this.queue.push({ method, args });
      return;
    }
    this._dispatch(method, args);
  }

  trackPageView(path, title) {
    this._call("trackPageView", [path, title]);
  }

  trackEvent(name, params) {
    this._call("trackEvent", [name, params]);
  }

  setUser(userId) {
    this._call("setUser", [userId]);
  }

  setUserProperties(properties) {
    this._call("setUserProperties", [properties]);
  }

  /** Clears identity — call on logout. */
  reset() {
    this.setUser(null);
  }
}

// Single shared instance for the whole app (React tree and sagas alike).
const analyticsService = new AnalyticsService();

export { AnalyticsService };
export default analyticsService;
